import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { CheckoutCard, type CheckoutIntent } from '@/components/checkout/CheckoutCard';
import { openRazorpayCheckout, RAZORPAY_THEME_COLOR, getCheckoutImage, type RazorpayResponse } from '@/lib/razorpay';

/**
 * OnboardingCheckout (/welcome) — the post-signup pay-now / pay-later screen.
 *
 * Reads the server-recorded OnboardingIntent (live re-validated: current price +
 * whether the offer lapsed) and renders the shared "statement" CheckoutCard on
 * the cream canvas. "Pay & unlock now" opens Razorpay Checkout for the
 * pre-selected plan RIGHT HERE (same flow as clicking the plan on /plans) —
 * it does NOT bounce the user back to the plan picker. "Pay later" snoozes the
 * intent and drops the user into the app on Free. Nothing is gated behind payment.
 */
export function OnboardingCheckout() {
  const navigate = useNavigate();
  const [intent, setIntent] = useState<CheckoutIntent | null>(null);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await api.get<{ intent: CheckoutIntent | null }>('/api/v2/onboarding/intent');
        if (!alive) return;
        if (!r.intent || !r.intent.plan) { navigate('/', { replace: true }); return; }
        setIntent(r.intent);
      } catch {
        // A thrown request (500 / network / auth) must NOT masquerade as
        // "nothing to buy" and silently drop the user on /chat — that hides a
        // real backend problem (e.g. a cross-DB plan mismatch). Surface a retry;
        // only a clean 200 with no pending intent redirects away (handled above).
        if (alive) setError('We could not load your offer right now.');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  /** Verify the Razorpay handshake on the backend, then drop the user into the
   *  product on their new plan. The authoritative webhook converges too. */
  async function finalizePayment(resp: RazorpayResponse): Promise<void> {
    setFlash('Confirming your payment…');
    try {
      const r = await api.post<{ status: 'PENDING' | 'PAID' | 'FAILED' }>('/api/v2/payments/callback', {
        razorpay_order_id: resp.razorpay_order_id,
        razorpay_payment_id: resp.razorpay_payment_id,
        razorpay_signature: resp.razorpay_signature,
      });
      if (r.status === 'FAILED') {
        setFlash('Payment was not completed — you can try again.');
        setActing(false);
        return;
      }
      // PAID (or PENDING — the webhook converges) → into the product on the new plan.
      navigate('/', { replace: true });
    } catch {
      setFlash('Could not confirm your payment. If you were charged, it will reflect shortly.');
      setActing(false);
    }
  }

  /** Open Razorpay for the pre-selected plan — mirrors Plans.select() exactly so
   *  the money path is identical to the /plans checkout (upgrade flag, coupon,
   *  100%-off shortcut, signed callback). */
  async function payNow() {
    if (!intent?.plan) return;
    const plan = intent.plan;
    setActing(true);
    setError(null);
    setFlash(null);
    try {
      // Determine "upgrade" the same way /plans does (an existing, different plan).
      let upgrade = false;
      try {
        const me = await api.get<{ subscription: { plan?: { id?: string } } | null }>('/api/v2/subscriptions/me');
        const existingPlanId = me.subscription?.plan?.id ?? null;
        upgrade = existingPlanId != null && existingPlanId !== plan.id;
      } catch { /* best-effort — default to a fresh purchase */ }

      // Only pass the coupon while the offer is live.
      const useCoupon = intent.coupon && !intent.offerExpired ? intent.coupon.code : undefined;
      const order = await api.post<{ orderId: string; keyId: string; amount: number; currency: string; activated?: boolean }>(
        `/api/v2/payments/initiate${upgrade ? '?upgrade=true' : ''}`,
        { planId: plan.id, ...(useCoupon ? { couponCode: useCoupon } : {}) },
      );

      // 100%-off / below-minimum coupon: the server activated directly — no Razorpay.
      if (order.activated) { navigate('/', { replace: true }); return; }

      let handled = false;
      await openRazorpayCheckout({
        key: order.keyId,
        order_id: order.orderId,
        amount: order.amount,
        currency: order.currency,
        name: 'BREWW AI',
        description: 'Product of Datacaffe.ai',
        image: getCheckoutImage(),
        theme: { color: RAZORPAY_THEME_COLOR },
        handler: (resp) => { handled = true; void finalizePayment(resp); },
        modal: {
          ondismiss: () => {
            if (handled) return;
            setActing(false);
            setFlash('Checkout was closed before payment completed.');
          },
        },
      });
    } catch (e) {
      setFlash((e as Error).message || 'Could not start checkout — please try again.');
      setActing(false);
    }
  }

  async function payLater() {
    setActing(true);
    try { await api.post('/api/v2/onboarding/intent/snooze', {}); } catch { /* best-effort */ }
    navigate('/', { replace: true });
  }

  if (loading) return <div className="rcpt-canvas"><p className="rcpt-canvas__muted">Loading…</p></div>;
  if (error) return (
    <div className="rcpt-canvas">
      <div className="rcpt-canvas__muted" style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
        <p>{error}</p>
        <button type="button" onClick={() => window.location.reload()}>Try again</button>
      </div>
    </div>
  );
  if (!intent?.plan) return null;

  return (
    <div className="rcpt-canvas">
      {flash && <p className="rcpt-canvas__muted" style={{ marginBottom: 12, textAlign: 'center' }}>{flash}</p>}
      <CheckoutCard
        plan={intent.plan}
        coupon={intent.coupon}
        offerExpired={intent.offerExpired}
        onPayNow={payNow}
        onSecondary={payLater}
        secondaryLabel="Pay later — continue on Free"
        busy={acting}
      />
    </div>
  );
}
